import {
  CreateToolAlternativeTool,
  CreateToolContact,
  CreateToolDocumentationLink,
  ToolUpsertRequest,
} from '../models/create-tool-request.model';
import { LifeCyclePhase } from '../models/life-cycle-phase.enum';
import { ContactType, LaunchType } from '../models/tool-form-types';
import { ToolStatus } from '../models/tool-status.enum';

export interface ToolFormStep1Value {
  name: string;
  serviceId: string;
  url: string;
  status: ToolStatus;
  launchType: LaunchType | '';
  shortDescription: string;
  description: string;
  previewImage: File | null;
}

export interface ToolFormStep2Value {
  personaIds: number[];
  lifeCyclePhaseIds: LifeCyclePhase[];
  toolTypeIds: number[];
  specialCondition: string;
  galleryImages: File[];
}

export interface DocumentationLinkValue {
  title: string;
  url: string;
  description: string;
}

export interface AlternativeToolValue {
  relatedToolId: number | null;
  relationshipType: string;
}

export interface ContactValue {
  type: ContactType;
  name: string;
  contactDetail: string;
}

/** Maps form values to a ToolUpsertRequest payload. */
export interface BuildCreateToolPayloadOptions {
  step1: ToolFormStep1Value;
  step2: ToolFormStep2Value;
  documentationLinks: DocumentationLinkValue[];
  alternativeTools: AlternativeToolValue[];
  contacts: ContactValue[];
  contactTypeIdMap: Record<ContactType, number>;
  temporaryBlobId?: string;
  previewReferenceOverride?: string;
  galleryReferencesOverride?: string[];
}

export function buildCreateToolPayload(options: BuildCreateToolPayloadOptions): ToolUpsertRequest {
  const {
    step1,
    step2,
    documentationLinks,
    alternativeTools,
    contacts,
    contactTypeIdMap,
    temporaryBlobId,
    previewReferenceOverride,
    galleryReferencesOverride,
  } = options;
  const documentationLinksPayload: CreateToolDocumentationLink[] = documentationLinks
    .filter(d => d.title.trim() && d.url.trim())
    .map(d => ({
      title: d.title.trim(),
      url: d.url.trim(),
    }));

  const alternativeToolsPayload: CreateToolAlternativeTool[] = alternativeTools
    .filter(a => a.relatedToolId != null)
    .map(a => ({
      relatedToolId: a.relatedToolId!,
      relationshipType: a.relationshipType,
    }));

  const contactsPayload: CreateToolContact[] = contacts
    .filter(ct => ct.type == ContactType.SupportLink ? ct.name.trim() && ct.contactDetail.trim() : ct.name.trim())
    .map(ct => ({
      name: ct.name.trim(),
      contactDetail: String(ct.contactDetail ?? '').trim(),
      role: ct.type,
      contactTypeId: contactTypeIdMap[ct.type] ?? 0,
    }));

  const previewReference = (previewReferenceOverride !== undefined)
    ? String(previewReferenceOverride).trim()
    : (step1.previewImage?.name?.trim() || undefined);

  const galleryReferences = (galleryReferencesOverride ?? (step2.galleryImages ?? []).map(file => file.name))
    .map(name => String(name ?? '').trim())
    .filter((name): name is string => Boolean(name));

  const serviceId = coerceServiceId(step1.serviceId);

  return {
    name: step1.name.trim(),
    ...(serviceId != null ? { serviceId } : {}),
    url: step1.url.trim(),
    status: step1.status ?? ToolStatus.Active,
    shortDescription: step1.shortDescription.trim(),
    description: step1.description,
    specialCondition: step2.specialCondition?.trim() || undefined,
    launchType: step1.launchType || undefined,
    previewReference,
    galleryReferences: galleryReferences.length ? galleryReferences : undefined,
    lifeCyclePhaseIds: (step2.lifeCyclePhaseIds ?? []).map(Number),
    personaIds: step2.personaIds?.length ? step2.personaIds : undefined,
    toolTypeIds: step2.toolTypeIds?.length ? step2.toolTypeIds : undefined,
    documentationLinks: documentationLinksPayload.length ? documentationLinksPayload : undefined,
    alternativeTools: alternativeToolsPayload.length ? alternativeToolsPayload : undefined,
    contacts: contactsPayload.length ? contactsPayload : undefined,
    temporaryBlobId: temporaryBlobId?.trim() || undefined,
  };
}

function coerceServiceId(value: string | number | null | undefined): number | string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const trimmed = String(value).trim();
  if (!trimmed) return undefined;

  // Numeric-only => send as number for backwards compatibility.
  if (/^\d+$/.test(trimmed)) {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  // Anything else => send as string.
  return trimmed;
}
